import { Link } from 'react-router-dom';
import { formatCurrency } from '@/lib/formatters';
import type { BrokerMonth, CreditLine, CreditMonthLines } from '@/lib/api/client';

export interface CreditMonthLinesViewProps {
  data: CreditMonthLines;
}

const money = (value: number) => formatCurrency(value, { cents: true });

/**
 * '2026-09-10' → 'Sep 10'. A day LABEL, already resolved in the user's zone by the API: built from
 * an explicit UTC instant and formatted in UTC, or it reads as the 9th in every negative offset.
 */
export function formatDayLabel(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })
    .format(new Date(Date.UTC(year, month - 1, date)));
}

const SIDE_WORDS: Record<NonNullable<CreditLine['side']>, string> = {
  put: 'put side', call: 'call side', both: 'both sides',
};

/**
 * What the event was, from its structured fields — not the journal's sentence, which prints a
 * roll's strikes once per fill lot and says "collected" for a debit open.
 */
export function describeCreditLine(line: CreditLine): string {
  const side = line.side ? SIDE_WORDS[line.side] : null;
  if (line.eventType === 'opened') {
    return `Opened ${line.strategyLabel ? line.strategyLabel.toLowerCase() : 'position'}`;
  }
  if (line.eventType === 'rolled') {
    const to = line.newExpiration ? ` to ${formatDayLabel(line.newExpiration)}` : '';
    return `Rolled${side ? ` ${side}` : ''}${to}`;
  }
  if (line.eventType === 'closed') return `Closed${side ? ` ${side}` : ''}`;
  return line.eventType;
}

export type AuditState = 'tied' | 'off' | 'none';

/** A cent either way is a tie; the two sides round independently. */
export function auditState(
  data: Pick<CreditMonthLines, 'openingCash' | 'closingCash'>, broker: BrokerMonth | null,
): AuditState {
  if (broker === null) return 'none';
  const tied = Math.abs(data.openingCash - broker.opening) < 0.01
    && Math.abs(data.closingCash - broker.closing) < 0.01;
  return tied ? 'tied' : 'off';
}

const signed = (value: number) => formatCurrency(value, { cents: true, showSign: true });

/**
 * Schwab says / Steward says. The broker figures come from its cash ledger and never pass
 * through the event pipeline, so a tie here is independent evidence and a gap is a real
 * question. Three states, kept distinct: tied, off (with both differences), and no broker rows —
 * pre-backfill history is neither a pass nor a fail.
 */
export function CreditMonthAudit({ data }: { data: CreditMonthLines }) {
  const state = auditState(data, data.broker);
  if (state === 'none' || data.broker === null) {
    return (
      <p data-testid="credit-audit" data-state="none" className="text-xs text-gray-500">
        No broker rows for this month, so nothing to check it against.
      </p>
    );
  }
  const broker = data.broker;
  const openingDiff = Math.round((data.openingCash - broker.opening) * 100) / 100;
  const closingDiff = Math.round((data.closingCash - broker.closing) * 100) / 100;
  return (
    <div
      data-testid="credit-audit"
      data-state={state}
      className={`rounded px-3 py-2 text-xs ${
        state === 'tied' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'
      }`}
    >
      <span className="font-medium">{state === 'tied' ? 'Ties to Schwab.' : 'Does not tie to Schwab.'}</span>{' '}
      Schwab: sold <span data-testid="audit-broker-opening">{money(broker.opening)}</span>, paid{' '}
      <span data-testid="audit-broker-closing">{money(broker.closing)}</span> on {broker.rows} fills
      {broker.fees > 0 && <> (fees {money(broker.fees)}, in neither figure)</>}.
      {' '}Steward: sold <span data-testid="audit-steward-opening">{money(data.openingCash)}</span>, paid{' '}
      <span data-testid="audit-steward-closing">{money(data.closingCash)}</span>.
      {state === 'off' && (
        <>
          {' '}Steward is off by <span data-testid="audit-opening-diff">{signed(openingDiff)}</span> sold and{' '}
          <span data-testid="audit-closing-diff">{signed(closingDiff)}</span> paid.
        </>
      )}
    </div>
  );
}

/**
 * The events behind one month's credit. Stacked rows, not a wide table: five columns do not fit
 * a phone, and this opens inside a table cell that must not scroll sideways.
 */
export function CreditMonthLinesView({ data }: CreditMonthLinesViewProps) {
  if (data.lines.length === 0) {
    // Still audited: a month with no events but broker rows is the loudest gap there is.
    return (
      <div className="space-y-2 whitespace-normal">
        <p data-testid="credit-lines-empty" className="text-sm text-gray-500">
          No credit or cost this month.
        </p>
        <CreditMonthAudit data={data} />
      </div>
    );
  }

  return (
    <div data-testid="credit-lines" className="space-y-1 whitespace-normal">
      <ul className="divide-y divide-gray-100">
        {data.lines.map((line) => (
          <li
            key={line.fingerprint}
            data-testid="credit-line"
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-sm"
          >
            <span className="min-w-0">
              <span data-testid="line-day" className="text-gray-500 mr-2">{formatDayLabel(line.day)}</span>
              <Link
                to={`/chains/${line.chainId}`}
                data-testid="line-chain"
                className="font-medium text-brand-600 hover:underline mr-2"
              >
                {line.symbol}
              </Link>
              <span data-testid="line-what" className="text-gray-700">{describeCreditLine(line)}</span>
            </span>
            <span className="flex gap-3 tabular-nums">
              {line.gross > 0 && (
                <span data-testid="line-gross" className="text-green-700">
                  <span className="text-xs text-gray-500 mr-1">credit</span>{money(line.gross)}
                </span>
              )}
              {line.costs > 0 && (
                <span data-testid="line-costs" className="text-red-600">
                  <span className="text-xs text-gray-500 mr-1">cost</span>{money(line.costs)}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap justify-end gap-x-4 gap-y-1 border-t border-gray-200 pt-2 text-sm">
        <span>Credit <span data-testid="lines-gross" className="font-semibold">{money(data.gross)}</span></span>
        <span>Costs <span data-testid="lines-costs" className="font-semibold">{money(data.costs)}</span></span>
        <span>Net <span data-testid="lines-net" className="font-semibold">{money(data.net)}</span></span>
      </div>

      <CreditMonthAudit data={data} />

      {data.unlisted > 0 && (
        <p data-testid="lines-unlisted" className="text-xs text-gray-500">
          {data.unlisted} {data.unlisted === 1 ? 'event' : 'events'} not listed: expirations and
          assignments move no cash, so they add to neither figure.
        </p>
      )}
    </div>
  );
}
